// Sidebar — fixed 232px dark left rail.
//
// Per shell-strings.md:
//   - Brand block (top): mark + wordmark + sub
//   - 5 sections × 15 items, filtered by RBAC visibility
//   - Footer: EnvBadge + region label
//
// Item visibility is per-role; uses navModel.visibleNav(roles). The signed-in
// user's roles come from useSession(); if the session isn't loaded yet we
// render no items (the shell loading state covers the gap visually).

import { NavLink } from 'react-router-dom';
import { GlyphMark } from '@gc-coach/design-system';
import { isRoleKey } from '@gc-coach/shared';
import { useSession } from '../auth/index.js';
import { useI18n } from '../i18n/index.js';
import { EnvBadge, type EnvLabel } from './EnvBadge.js';
import { visibleNav, type NavItemKey, type SectionKey } from './navModel.js';
import styles from './Sidebar.module.css';

interface SidebarProps {
  /** Currently-active nav item — drives the visual active state. */
  activeKey?: NavItemKey;
  /** Environment for the footer EnvBadge. */
  env: EnvLabel;
}

const SECTION_LABEL_KEY: Record<SectionKey, string> = {
  overview: 'shell.sectionOverview',
  knowledge: 'shell.sectionKnowledge',
  insights: 'shell.sectionInsights',
  customers: 'shell.sectionCustomers',
  administration: 'shell.sectionAdministration',
};

const ITEM_LABEL_KEY: Record<NavItemKey, string> = {
  dashboard: 'shell.navDashboard',
  analytics: 'shell.navAnalytics',
  notifications: 'shell.navNotifications',
  sources: 'shell.navSources',
  review: 'shell.navReview',
  kb: 'shell.navKb',
  frameworks: 'shell.navFrameworks',
  personae: 'shell.navPersonae',
  benchmark: 'shell.navBenchmark',
  rag: 'shell.navRag',
  labs: 'shell.navLabs',
  tenants: 'shell.navTenants',
  moderation: 'shell.navModeration',
  billing: 'shell.navBilling',
  users: 'shell.navUsers',
  audit: 'shell.navAudit',
  settings: 'shell.navSettings',
};

export function Sidebar({ activeKey, env }: SidebarProps) {
  const { t } = useI18n();
  const { session } = useSession();
  // session.roles is typed as the union (RoleKey | ConsumerRoleKey) since
  // /auth/me serves both portals. Narrow to RoleKey via isRoleKey; the
  // admin SPA only ever sees portal='admin' so this filter is a no-op in
  // practice but satisfies the visibleNav signature.
  const roles = (session?.roles ?? []).filter(isRoleKey);
  const sections = visibleNav(roles);

  return (
    <aside className={styles.sidebar} aria-label="Primary navigation">
      <header className={styles.brand}>
        <span className={styles.brandTile} aria-hidden="true">
          <GlyphMark width={22} height={22} />
        </span>
        <span className={styles.brandText}>
          <span className={styles.brandWordmark}>{t('shell.brandWordmark')}</span>
          <span className={styles.brandSub}>{t('shell.brandSub')}</span>
        </span>
      </header>

      <nav className={styles.nav}>
        {sections.map((section) => (
          <section key={section.key} className={styles.section}>
            <h2 className={styles.sectionLabel}>{t(SECTION_LABEL_KEY[section.key])}</h2>
            <ul className={styles.itemList}>
              {section.items.map((item) => (
                <li key={item.key}>
                  <NavLink
                    to={item.to}
                    end
                    className={({ isActive }) =>
                      [styles.item, isActive || activeKey === item.key ? styles.itemActive : null]
                        .filter(Boolean)
                        .join(' ')
                    }
                  >
                    {t(ITEM_LABEL_KEY[item.key])}
                  </NavLink>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </nav>

      <footer className={styles.footer}>
        <EnvBadge env={env} />
        <span className={styles.regionLabel}>{t('shell.regionLabel')}</span>
      </footer>
    </aside>
  );
}
